import { Autocomplete, Badge } from '@mui/joy';
import { useContext, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router';
import { useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import { AppContext } from '@/shared/context/context';
import { localStorageKeys } from '@/shared/utils/constants';
import ROUTES from '@/containers/App/Router/Routes';
import { trpc } from '@/config/trpc';
import virtualAutocompleteAdapter from '@/shared/utils/virtualAutocompleteAdapter';

import useAcceptTeamInvitation from '@/mutations/team/useAcceptTeamInvitation';
import useNotification from '@/shared/hooks/UseNotification';

import { InlineSpinner, TeamIcon } from '@/shared/icons/Icons';

const TopbarTeam = () => {
  const [selectedTeamId, setSelectedTeamId] = useState<string | null>(null);

  const { currentTeam, setUi } = useContext(AppContext);
  const { sendSuccessMessage } = useNotification();
  const { t } = useTranslation();

  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const utils = trpc.useUtils();

  const { data: teamsAndInvitations, isLoading } = trpc.team.teamsAndInvitations.useQuery();
  const { acceptTeamInvitationAsync } = useAcceptTeamInvitation();

  const badgeCount = useMemo(() => {
    const invitations = teamsAndInvitations?.filter((team) => team.invitationId);
    return invitations?.length || null;
  }, [teamsAndInvitations]);

  useEffect(() => {
    if (currentTeam) {
      setSelectedTeamId(currentTeam.id);
    }
  }, [teamsAndInvitations, currentTeam]);

  return (
    <div>
      <Badge badgeContent={badgeCount} badgeInset="0">
        <Autocomplete
          {...virtualAutocompleteAdapter.baseProps}
          className="w-72"
          disableClearable={!isLoading}
          disabled={isLoading}
          getOptionKey={(option) => option.id}
          groupBy={(option) => option.type}
          loading={isLoading}
          options={teamsAndInvitations ?? []}
          startDecorator={isLoading ? <InlineSpinner /> : <TeamIcon />}
          value={teamsAndInvitations?.find((option) => option.value === selectedTeamId) ?? null}
          onChange={async (_e, option) => {
            if (!option) return;

            if (option.invitationId) {
              await acceptTeamInvitationAsync({
                teamId: option.value as string,
                teamInvitationId: option.invitationId,
              });
            }

            setSelectedTeamId(option.value as string);
            // The tRPC links read the team header from localStorage, and the ui reducer only writes it on
            // the next render, so the refetches triggered by invalidate would still go out for the old team.
            localStorage.setItem(localStorageKeys.teamId, option.value as string);
            setUi({
              payload: option.value,
              type: 'SAVE_CURRENT_TEAM_ID',
            });

            // Query keys don't contain the team: drop cached data of the old team so no page renders it
            // while refetching. The affiliations stay mounted app-wide, so they're reset instead.
            queryClient.removeQueries({ type: 'inactive' });
            utils.finance.keypointInvoicing.affiliations.list.reset();
            utils.invalidate();
            navigate(ROUTES.HOME);

            sendSuccessMessage(t('successfullyChangedToTheTeam', { name: option.label }));
          }}
        />
      </Badge>
    </div>
  );
};

export default TopbarTeam;
